import type { PrimaryKey } from '@aigc/types';
import { mutationOptions, queryOptions } from '@tanstack/react-query';
import qs from 'qs';
import { http } from '../common/http-client';
import type {
  ScriptTemplateCreateDto,
  ScriptTemplateListResponse,
  ScriptTemplateQueryDto,
  ScriptTemplateResponse,
  ScriptTemplateUpdateDto,
} from './model';

const script_template_query_key = ['脚本模版库'] as const;

export const queryScriptTemplates = async (params: ScriptTemplateQueryDto) => {
  const search_params = qs.stringify(
    {
      ...params,
      product_name: params.product_name || undefined,
      tags: params.tags?.length ? params.tags : undefined,
      tag_ids: params.tag_ids?.length ? params.tag_ids : undefined,
      product_category_ids: params.product_category_ids?.length
        ? params.product_category_ids
        : undefined,
      hook_type_ids: params.hook_type_ids?.length
        ? params.hook_type_ids
        : undefined,
      selling_point_ids: params.selling_point_ids?.length
        ? params.selling_point_ids
        : undefined,
      script_language_ids: params.script_language_ids?.length
        ? params.script_language_ids
        : undefined,
    },
    {
      arrayFormat: 'repeat',
      skipNulls: true,
    },
  );

  return http
    .get('script-templates', { searchParams: search_params })
    .json<ScriptTemplateListResponse>();
};

export const getScriptTemplatesQueryOptions = (
  params: ScriptTemplateQueryDto,
) => {
  return queryOptions({
    queryKey: [...script_template_query_key, '列表', params],
    queryFn: async () => {
      return queryScriptTemplates(params);
    },
  });
};

export const getScriptTemplate = async (id: number | string) => {
  return http.get(`script-templates/${id}`).json<ScriptTemplateResponse>();
};

export const getScriptTemplateQueryOptions = (id: number | string) => {
  return queryOptions({
    queryKey: [...script_template_query_key, '详情', id],
    queryFn: async () => {
      return getScriptTemplate(id);
    },
  });
};

export const createScriptTemplate = async (data: ScriptTemplateCreateDto) => {
  return http
    .post('script-templates', { json: data })
    .json<ScriptTemplateResponse>();
};

export const createScriptTemplateMutationOptions = mutationOptions({
  mutationKey: [...script_template_query_key, '创建'],
  meta: {
    invalidates: [{ queryKey: script_template_query_key }],
  },
  mutationFn: async (data: ScriptTemplateCreateDto) => {
    return createScriptTemplate(data);
  },
});

export const updateScriptTemplate = async (
  id: PrimaryKey,
  data: ScriptTemplateUpdateDto,
) => {
  return http
    .patch(`script-templates/${id}`, { json: data })
    .json<ScriptTemplateResponse>();
};

export const updateScriptTemplateMutationOptions = mutationOptions({
  mutationKey: [...script_template_query_key, '更新'],
  meta: {
    invalidates: [{ queryKey: script_template_query_key }],
  },
  mutationFn: async (payload: {
    id: PrimaryKey;
    data: ScriptTemplateUpdateDto;
  }) => {
    return updateScriptTemplate(payload.id, payload.data);
  },
});

export const deleteScriptTemplate = async (id: PrimaryKey) => {
  await http.delete(`script-templates/${id}`);
};

export const deleteScriptTemplateMutationOptions = mutationOptions({
  mutationKey: [...script_template_query_key, '删除'],
  meta: {
    invalidates: [{ queryKey: script_template_query_key }],
  },
  mutationFn: async (id: PrimaryKey) => {
    await deleteScriptTemplate(id);
  },
});
